/**
 * UNIVERSAL TABLE - MAIN COMPONENT
 * The ultimate table component for all forms
 * @version 1.0.1
 * @production-ready
 * Fixed: Table width calculation issue causing white space on right side
 */

import React, { useMemo, useLayoutEffect, useState, useEffect, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { UniversalTableProps, DEFAULT_STYLE_CONFIG } from '../types';
import { useColumnWidth } from '../hooks';
import { TableHeader } from './TableHeader';
import { TableRow } from './TableRow';
export const UniversalTable: React.FC<UniversalTableProps> = ({
  columns,
  data,
  rowKeys,
  styleConfig = DEFAULT_STYLE_CONFIG,
  focusedCell,
  loading = false,
  error,
  topRowContent,
  onCellChange,
  onRowKeyChange,
  onRemoveRowKey,
  onKeyDown,
  onFocus,
  onRowClick,
  setInputRef,
  containerRef,
  customCellRenderer,
  customRowRenderer
}) => {
  // Merge with default config
  const finalStyleConfig = useMemo(() => ({
    ...DEFAULT_STYLE_CONFIG,
    ...styleConfig
  }), [styleConfig]);

  // Calculate column widths (keyed off rowKeys, not data — see useColumnWidth.ts)
  const { columnWidths, totalWidth, columnsWithSticky } = useColumnWidth(
    columns,
    data,
    finalStyleConfig,
    rowKeys
  );

  // Keep frontend rendering-only: calculated values should come from backend payloads.
  const dataWithCalculated = data;

  // Filter visible columns
  const visibleColumns = useMemo(() =>
    columnsWithSticky.filter(col => !col.hidden),
    [columnsWithSticky]
  );

  // Filter column widths to match visible columns
  const baseVisibleColumnWidths = useMemo(() => {
    return columnsWithSticky
      .map((col, index) => ({ col, index, width: columnWidths[index] }))
      .filter(({ col }) => !col.hidden)
      .map(({ width }) => width);
  }, [columnsWithSticky, columnWidths]);

  // Track container width to ensure table expands properly
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  // Row virtualization: only rows near the viewport are mounted as real DOM nodes,
  // no matter how many rows are loaded. This replaces the old manual "grow-only"
  // render window (which never unmounted rows and let DOM nodes accumulate without
  // bound on large datasets). The scroll element stays `containerRef` — the same node
  // each form already attaches its own scroll/wheel listeners to for network lazy-load,
  // so that mechanism (load older rows near the top) keeps working unchanged.
  const rowHeight = finalStyleConfig.rowHeight ?? (finalStyleConfig.condensed ? 28 : 38);
  const getScrollElement = useCallback(() => containerRef?.current ?? null, [containerRef]);
  const estimateRowSize = useCallback(() => rowHeight, [rowHeight]);
  const rowVirtualizer = useVirtualizer({
    count: rowKeys.length,
    getScrollElement,
    estimateSize: estimateRowSize,
    overscan: 12
  });

  // Scroll to bottom ONCE on first data render so user sees newest rows
  const hasInitialScrolledRef = useRef(false);
  useLayoutEffect(() => {
    // Only run once: after loading finishes and rows exist
    if (hasInitialScrolledRef.current) return;
    const container = containerRef?.current;
    if (!container || loading || rowKeys.length === 0) return;

    // Jump to bottom instantly (no animation)
    container.style.scrollBehavior = 'auto';
    container.scrollTop = container.scrollHeight - container.clientHeight;
    hasInitialScrolledRef.current = true;

    // Restore smooth scroll after paint
    requestAnimationFrame(() => {
      if (container) container.style.scrollBehavior = 'smooth';
    });
  }, [loading, rowKeys.length, containerRef]);

  // Calculate adjusted column widths when container is wider than total width
  const visibleColumnWidths = useMemo(() => {
    if (containerWidth === null || containerWidth <= totalWidth) {
      return baseVisibleColumnWidths;
    }

    // Container is wider than total width - distribute extra space proportionally
    const extraSpace = containerWidth - totalWidth;
    const totalBaseWidth = baseVisibleColumnWidths.reduce((sum, w) => sum + w, 0);

    if (totalBaseWidth === 0) return baseVisibleColumnWidths;

    // Distribute extra space proportionally to each column
    const adjustedWidths = baseVisibleColumnWidths.map(width => {
      const proportion = width / totalBaseWidth;
      const additionalWidth = extraSpace * proportion;
      return Math.round(width + additionalWidth);
    });

    // Ensure the sum equals containerWidth exactly (fix rounding errors)
    const currentSum = adjustedWidths.reduce((sum, w) => sum + w, 0);
    const difference = containerWidth - currentSum;
    if (difference !== 0 && adjustedWidths.length > 0) {
      // Add the difference to the last column to ensure exact match
      adjustedWidths[adjustedWidths.length - 1] += difference;
    }

    return adjustedWidths;
  }, [baseVisibleColumnWidths, containerWidth, totalWidth]);

  // Use layout effect to measure container width after render
  useLayoutEffect(() => {
    if (!containerRef?.current) return;

    const updateWidth = () => {
      if (containerRef?.current) {
        const width = containerRef.current.offsetWidth;
        // Only update if width is valid and greater than 0
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    };

    // Initial measurement with multiple attempts to catch container width
    // Use requestAnimationFrame to ensure DOM is fully laid out
    let rafId: number;
    let timeoutId1: ReturnType<typeof setTimeout>;
    let timeoutId2: ReturnType<typeof setTimeout>;

    // Immediate check
    updateWidth();

    // Check after animation frame
    rafId = requestAnimationFrame(() => {
      updateWidth();
      // Check again after short delays to catch any late layout changes
      timeoutId1 = setTimeout(updateWidth, 50);
      timeoutId2 = setTimeout(updateWidth, 200);
    });

    // Create resize observer to handle container size changes. Defer the actual
    // measurement/state-update to the next frame via requestAnimationFrame — updating
    // state synchronously inside a ResizeObserver callback can itself change layout
    // within the same observation cycle, which is what triggers the browser's benign
    // "ResizeObserver loop completed with undelivered notifications" warning.
    let resizeRafId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeRafId !== null) cancelAnimationFrame(resizeRafId);
      resizeRafId = requestAnimationFrame(updateWidth);
    });

    resizeObserver.observe(containerRef.current);

    // Fallback: also listen to window resize
    window.addEventListener('resize', updateWidth);

    return () => {
      cancelAnimationFrame(rafId);
      if (resizeRafId !== null) cancelAnimationFrame(resizeRafId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [containerRef]);

  // Don't show empty state - always show table structure with headers
  // This allows users to see column headers and add rows even when there's no data
  // Header should always be visible, even during loading/error states

  const virtualRowItems = rowVirtualizer.getVirtualItems();
  const virtualTotalSize = rowVirtualizer.getTotalSize();
  const virtualPaddingTop = virtualRowItems.length > 0 ? virtualRowItems[0].start : 0;
  const virtualPaddingBottom = virtualRowItems.length > 0
    ? virtualTotalSize - virtualRowItems[virtualRowItems.length - 1].end
    : 0;

  return (
    <div
      ref={containerRef}
      className="universal-table-container w-full h-full"
      style={{
        scrollBehavior: 'smooth',
        background: 'var(--color-white)',
        fontFamily: finalStyleConfig.fontFamily,
        position: 'relative',
        overflow: 'auto'
      }}
    >
      <style>{`
        .universal-table-container table { 
          border-collapse: separate !important; 
          border-spacing: 0 !important; 
          margin: 0 !important; 
          padding: 0 !important; 
        }
        .universal-table-container thead, 
        .universal-table-container tbody, 
        .universal-table-container tr, 
        .universal-table-container th, 
        .universal-table-container td { 
          border-spacing: 0 !important; 
          margin: 0 !important; 
          box-sizing: border-box !important; 
        }
        .universal-table-container thead {
          position: sticky !important;
          top: 0 !important;
          z-index: 20 !important;
        }
        .universal-table-container th { 
          border: 1px solid ${finalStyleConfig.borderColor || 'var(--color-gray-300)'} !important; 
          padding: 3px 4px !important; 
          background: var(--color-gray-200) !important;
          color: #000000 !important;
          font-weight: 700 !important;
        }
        .universal-table-container th *,
        .universal-table-container th span,
        .universal-table-container th div,
        .universal-table-container th div *,
        .universal-table-container thead th,
        .universal-table-container thead th *,
        .universal-table-container thead th span,
        .universal-table-container thead th div,
        .universal-table-container thead th div *,
        .universal-table-container table thead th,
        .universal-table-container table thead th *,
        .universal-table-container table thead th span {
          color: #000000 !important;
          font-weight: 700 !important;
        }
        /* Force all text nodes in headers - multiple rules for maximum coverage */
        .universal-table-container thead th {
          color: #000000 !important;
          font-weight: 700 !important;
        }
        .universal-table-container table thead th {
          color: #000000 !important;
          font-weight: 700 !important;
        }
        .universal-table-container td { 
          border: 1px solid ${finalStyleConfig.borderColor || 'var(--color-gray-300)'} !important; 
          padding: 0 !important; 
        }
        /* Ensure sticky body cells work correctly - left position comes from inline styles */
        .universal-table-container tbody td[data-sticky="true"] {
          position: sticky !important;
          z-index: 10 !important;
          box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
          border-right: 2px solid var(--color-gray-400) !important;
        }
        /* Ensure left property from inline styles is not overridden */
        .universal-table-container tbody td[data-sticky="true"][style*="left"] {
          /* Inline styles take precedence, this rule ensures compatibility */
        }
        /* Background colors are set via inline styles to support striped rows */
        .universal-table-container tbody tr:hover td[data-sticky="true"] {
          background: var(--color-primary-blue-light) !important;
        }
        /* Ensure table structure supports sticky positioning */
        .universal-table-container table {
          border-collapse: separate !important;
          border-spacing: 0 !important;
        }
        /* Force all inputs to be normal weight (400) - override inline styles - MAXIMUM SPECIFICITY */
        .universal-table-container input:not([readonly]):not(:disabled),
        .universal-table-container input[type="text"]:not([readonly]):not(:disabled),
        .universal-table-container input[type="number"]:not([readonly]):not(:disabled),
        .universal-table-container input[type="date"]:not([readonly]):not(:disabled),
        .universal-table-container input[type="time"]:not([readonly]):not(:disabled),
        .universal-table-container td input:not([readonly]):not(:disabled),
        .universal-table-container td input[type="text"]:not([readonly]):not(:disabled),
        .universal-table-container td input[type="number"]:not([readonly]):not(:disabled),
        .universal-table-container tbody input:not([readonly]):not(:disabled),
        .universal-table-container tbody td input:not([readonly]):not(:disabled),
        .universal-table-container tbody td input[type="text"]:not([readonly]):not(:disabled),
        .universal-table-container tbody td input[type="number"]:not([readonly]):not(:disabled),
        .universal-table-container table td input:not([readonly]):not(:disabled),
        .universal-table-container table td input[type="text"]:not([readonly]):not(:disabled),
        .universal-table-container table td input[type="number"]:not([readonly]):not(:disabled),
        .universal-table-container table tbody td input:not([readonly]):not(:disabled),
        .universal-table-container table tbody td input[type="text"]:not([readonly]):not(:disabled),
        .universal-table-container table tbody td input[type="number"]:not([readonly]):not(:disabled) {
          font-weight: 400 !important;
          font-style: normal !important;
        }
        ${finalStyleConfig.stripedRows ? `
          .universal-table-container tbody tr:nth-child(even) {
            background: ${finalStyleConfig.alternateRowBgColor || 'var(--color-gray-50)'} !important;
          }
        ` : ''}
        /* Remove all borders from date inputs and completely hide native value */
        .universal-table-container input[type="date"],
        .universal-table-container input[type="date"].date-input-no-border,
        .universal-table-container td input[type="date"],
        .universal-table-container tbody td input[type="date"],
        .universal-table-container table td input[type="date"],
        .universal-table-container table tbody td input[type="date"] {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
          border-left: none !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
          color: transparent !important;
          caret-color: transparent !important;
          text-indent: -9999px !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          -webkit-text-fill-color: transparent !important;
          -moz-text-fill-color: transparent !important;
        }
        .universal-table-container input[type="date"]:focus,
        .universal-table-container input[type="date"].date-input-no-border:focus,
        .universal-table-container td input[type="date"]:focus,
        .universal-table-container tbody td input[type="date"]:focus,
        .universal-table-container table td input[type="date"]:focus,
        .universal-table-container table tbody td input[type="date"]:focus {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
          border-left: none !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
          color: transparent !important;
          caret-color: transparent !important;
          text-indent: -9999px !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          -webkit-text-fill-color: transparent !important;
          -moz-text-fill-color: transparent !important;
        }
        /* Hide native date input pseudo-elements that show month numbers */
        .universal-table-container input[type="date"]::-webkit-calendar-picker-indicator {
          opacity: 0 !important;
          pointer-events: none !important;
          position: absolute !important;
          right: 0 !important;
        }
        .universal-table-container input[type="date"]::-webkit-inner-spin-button,
        .universal-table-container input[type="date"]::-webkit-clear-button {
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Ensure overlay text is always visible and on top */
        .universal-table-container .date-display-text {
          pointer-events: none !important;
          z-index: 10 !important;
          position: absolute !important;
        }
        /* Remove all borders from time inputs */
        .universal-table-container input[type="time"],
        .universal-table-container input[type="time"].time-input-no-border,
        .universal-table-container td input[type="time"],
        .universal-table-container tbody td input[type="time"],
        .universal-table-container table td input[type="time"],
        .universal-table-container table tbody td input[type="time"] {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
          border-left: none !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
        }
        .universal-table-container input[type="time"]:focus,
        .universal-table-container input[type="time"].time-input-no-border:focus,
        .universal-table-container td input[type="time"]:focus,
        .universal-table-container tbody td input[type="time"]:focus,
        .universal-table-container table td input[type="time"]:focus,
        .universal-table-container table tbody td input[type="time"]:focus {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
          border-left: none !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
        }
        /* Remove borders from select dropdowns - comprehensive rules with maximum specificity */
        .universal-table-container select,
        .universal-table-container select.select-no-border,
        .universal-table-container select[class],
        .universal-table-container select[id],
        .universal-table-container td select,
        .universal-table-container td select.select-no-border,
        .universal-table-container tbody td select,
        .universal-table-container tbody td select.select-no-border,
        .universal-table-container table select,
        .universal-table-container table select.select-no-border,
        .universal-table-container table td select,
        .universal-table-container table td select.select-no-border,
        .universal-table-container table tbody td select,
        .universal-table-container table tbody td select.select-no-border {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
          border-left: none !important;
          outline: none !important;
          outline-width: 0 !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
        }
        .universal-table-container select:focus,
        .universal-table-container select.select-no-border:focus,
        .universal-table-container select[class]:focus,
        .universal-table-container select[id]:focus,
        .universal-table-container td select:focus,
        .universal-table-container td select.select-no-border:focus,
        .universal-table-container tbody td select:focus,
        .universal-table-container tbody td select.select-no-border:focus,
        .universal-table-container table select:focus,
        .universal-table-container table select.select-no-border:focus,
        .universal-table-container table td select:focus,
        .universal-table-container table td select.select-no-border:focus,
        .universal-table-container table tbody td select:focus,
        .universal-table-container table tbody td select.select-no-border:focus {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
          border-left: none !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
        }
        .universal-table-container select option,
        .universal-table-container select.select-no-border option {
          border: none !important;
          outline: none !important;
          border-width: 0 !important;
          border-style: none !important;
        }
        /* Remove left border from table cells containing time inputs */
        .universal-table-container tbody td:has(input[type="time"]),
        .universal-table-container tbody td:has(.time-input-no-border) {
          border-left: none !important;
          border-left-width: 0 !important;
          border-left-style: none !important;
          border-left-color: transparent !important;
        }
        /* Fallback for browsers that don't support :has() */
        .universal-table-container tbody td .time-input-no-border {
          margin-left: 0 !important;
        }
      `}</style>

      <div style={{
        minWidth: `${totalWidth}px`,
        width: '100%',
        maxWidth: '100%'
      }}>
        <table
          style={{
            borderCollapse: 'separate',
            borderSpacing: '0',
            tableLayout: 'fixed',
            width: containerWidth !== null && containerWidth > totalWidth ? `${containerWidth}px` : `${totalWidth}px`,
            minWidth: `${totalWidth}px`,
            margin: '0',
            padding: '0',
            fontSize: `${finalStyleConfig.baseFontSize}px`
          }}
        >
          <TableHeader
            columns={visibleColumns}
            columnWidths={visibleColumnWidths}
            styleConfig={finalStyleConfig}
            rowKeys={[]}
            onRowKeyChange={onRowKeyChange}
            onRemoveRowKey={onRemoveRowKey}
            topRowContent={topRowContent}
          />

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'var(--color-gray-600)',
                    background: 'var(--color-white)'
                  }}
                >
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="animate-spin h-8 w-8 border-4 border-primary-blue border-t-transparent rounded-full"></div>
                    <p style={{ color: 'var(--color-gray-600)' }}>Loading data...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'var(--color-status-error)',
                    background: 'var(--color-white)'
                  }}
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="text-4xl">⚠️</div>
                    <p className="text-lg font-semibold">Error Loading Data</p>
                    <p className="text-sm" style={{ color: 'var(--color-gray-600)' }}>{error}</p>
                  </div>
                </td>
              </tr>
            ) : rowKeys.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'var(--color-gray-500)',
                    fontStyle: 'italic'
                  }}
                >
                  No data available. Click "New Date" to add a row.
                </td>
              </tr>
            ) : (
              <>
                {virtualPaddingTop > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={visibleColumns.length}
                      style={{ height: `${virtualPaddingTop}px`, padding: 0, border: 'none' }}
                    />
                  </tr>
                )}
                {virtualRowItems.map((virtualRow) => {
                  const rowIndex = virtualRow.index;
                  const rowKey = rowKeys[rowIndex];
                  if (rowKey === undefined) return null;

                  if (customRowRenderer) {
                    return (
                      <React.Fragment key={`${rowIndex}-${rowKey}`}>
                        {customRowRenderer(rowKey, rowIndex, dataWithCalculated[rowKey])}
                      </React.Fragment>
                    );
                  }

                  return (
                    <TableRow
                      key={`${rowIndex}-${rowKey}`}
                      rowKey={rowKey}
                      rowIndex={rowIndex}
                      columns={visibleColumns}
                      columnWidths={visibleColumnWidths}
                      rowData={dataWithCalculated[rowKey] || {}}
                      styleConfig={finalStyleConfig}
                      focusedCell={focusedCell}
                      onCellChange={onCellChange}
                      onKeyDown={onKeyDown}
                      onFocus={onFocus}
                      setInputRef={setInputRef}
                      onRowClick={onRowClick}
                      onRemoveRowKey={onRemoveRowKey}
                    />
                  );
                })}
                {virtualPaddingBottom > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={visibleColumns.length}
                      style={{ height: `${virtualPaddingBottom}px`, padding: 0, border: 'none' }}
                    />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
